/**
 * Team Card Component
 * Part of TASK-334: Frontend Admin Panel
 *
 * Card displaying individual team information with actions.
 */

import React from 'react';
import { MoreVertical, Users, Briefcase, Pencil, UserPlus, Eye, Trash2 } from 'lucide-react';
import type { Team } from '../../../types/admin';

interface TeamCardProps {
  team: Team;
  onEdit: (team: Team) => void;
  onManageMembers: (team: Team) => void;
  onViewClients: (team: Team) => void;
  onDelete: (team: Team) => void;
}

const TeamCard: React.FC<TeamCardProps> = ({
  team,
  onEdit,
  onManageMembers,
  onViewClients,
  onDelete,
}) => {
  const [showMenu, setShowMenu] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`
      bg-white rounded-lg border p-4 hover:shadow-md transition-shadow
      ${team.is_active ? 'border-gray-200' : 'border-gray-200 bg-gray-50 opacity-75'}
    `}>
      <div className="flex items-start justify-between">
        {/* Team info */}
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="w-10 h-10 rounded-lg bg-[#002B45] text-[#F7B500] flex items-center justify-center">
            <Users size={20} />
          </div>

          {/* Details */}
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-gray-900">{team.name}</h3>
              {!team.is_active && (
                <span className="px-1.5 py-0.5 text-xs font-medium bg-gray-200 text-gray-600 rounded">
                  Inactive
                </span>
              )}
            </div>
            {team.description && (
              <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">
                {team.description}
              </p>
            )}
            {team.lead_display_name && (
              <p className="text-sm text-gray-600 mt-1">
                Lead: <span className="font-medium">{team.lead_display_name}</span>
              </p>
            )}
          </div>
        </div>

        {/* Actions menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
          >
            <MoreVertical size={18} />
          </button>

          {showMenu && (
            <div className="absolute right-0 mt-1 w-44 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
              <button
                onClick={() => {
                  onEdit(team);
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Pencil size={16} />
                Edit Team
              </button>

              <button
                onClick={() => {
                  onManageMembers(team);
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <UserPlus size={16} />
                Manage Members
              </button>

              <button
                onClick={() => {
                  onViewClients(team);
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Eye size={16} />
                View Clients
              </button>

              <div className="border-t border-gray-100 my-1" />

              <button
                onClick={() => {
                  onDelete(team);
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                <Trash2 size={16} />
                Delete Team
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-6">
        <div className="flex items-center gap-1.5 text-sm text-gray-500">
          <Users size={14} />
          <span>{team.member_count} member{team.member_count !== 1 ? 's' : ''}</span>
        </div>
        <div className="flex items-center gap-1.5 text-sm text-gray-500">
          <Briefcase size={14} />
          <span>{team.client_count} client{team.client_count !== 1 ? 's' : ''}</span>
        </div>
      </div>
    </div>
  );
};

export default TeamCard;
